import type { APC } from 'api/v2/users/APC';
import { Alert } from 'components/atoms/Alert';
import { Button } from 'components/atoms/Button';
import CountryFlag from 'components/atoms/CountryFlag';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import ProfessionSelect from 'components/atoms/Form/ProfessionSelect';
import { Select } from 'components/atoms/Form/Select';
import FileSearchIcon from 'components/atoms/SVG/FileSearchIcon.component';
import WalletIcon from 'components/atoms/SVG/WalletIcon.component';
import ModalHeading from 'components/molecules/ModalHeading';
import SteuernummerField from 'components/pages/Taxes/Tax/SteuernummerField';
import { joiResolver } from 'customJoi';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useFeatures from 'hooks/useFeatures';
import useSettingsSelectOptions from 'hooks/useSettingsSelectOptions';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AccountSettingsFieldsNamesEnum,
  MonthlyPaymentVolume,
} from 'types/settings.types';
import { AvailableCountries, type IUser } from 'types/users.types';
import { fadeTransition } from 'utils/constants';
import formatMoney from 'utils/formatMoney';
import { getSelectOptionValue } from 'utils/helpers';
import {
  constructAccountDetailsFormDefaultValues,
  normalizeMaskedInputs,
} from 'utils/settings/settingsHelpers';
import {
  BEOnboardingFormSchema,
  DEOnboardingFormSchema,
} from '../../utils/validation';

type OnboardingFormValues = IUser & {
  monthlyPaymentVolume: MonthlyPaymentVolume;
  companyName: string;
  additionalBusinessActivityDescription: string;
};

const BEForm: FC<{
  onConfirm: (values: OnboardingFormValues) => void;
  currentAccountDetailsValues?: IUser;
}> = ({ onConfirm, currentAccountDetailsValues }) => {
  const { t } = useTranslation();

  const settingsFeatures = useFeatures('settings');

  const { VATNumberMask, VATNumberMaskNormalizer } = settingsFeatures;

  const user = useGetCurrentCustomerData().data as IUser;

  const defaultValues = useMemo(
    () =>
      constructAccountDetailsFormDefaultValues(
        currentAccountDetailsValues || (user as IUser),
      ),
    [currentAccountDetailsValues, user],
  );

  const [currentStep, setCurrentStep] = useState(0);

  const schema = BEOnboardingFormSchema();

  const methods = useForm({
    defaultValues,
    resolver: joiResolver(schema, {
      allowUnknown: true,
      abortEarly: false,
    }),
  });

  const setAndValidate: typeof methods.setValue = (name, value, options) =>
    methods.setValue(name, value, { shouldValidate: true, ...options });

  const settingsOptions = useSettingsSelectOptions(
    user?.country as AvailableCountries,
  );

  const vatNumberPlaceholder = useFeatures('settings.vatNumberPlaceholder');

  const [
    APC1_value,
    APC2_value,
    occupation_value,
    streetValue,
    cityValue,
    countryValue,
    zipValue,
    VATNumberValue,
    firstNameValue,
    lastNameValue,
    emailValue,
  ] = methods.watch([
    AccountSettingsFieldsNamesEnum.APC1,
    AccountSettingsFieldsNamesEnum.APC2,
    AccountSettingsFieldsNamesEnum.occupation,
    AccountSettingsFieldsNamesEnum.street,
    AccountSettingsFieldsNamesEnum.city,
    AccountSettingsFieldsNamesEnum.country,
    AccountSettingsFieldsNamesEnum.zip,
    AccountSettingsFieldsNamesEnum.VATNumber,
    AccountSettingsFieldsNamesEnum.firstName,
    AccountSettingsFieldsNamesEnum.lastName,
    AccountSettingsFieldsNamesEnum.email,
  ]);

  const steps = [
    <motion.div {...fadeTransition} key="form_1" className="w-full">
      <ModalHeading
        Icon={WalletIcon}
        title={t('accountable_banking.onboarding.form.title')}
        subtitle={t('accountable_banking.onboarding.form.subtitle')}
      />

      <FormProvider {...methods}>
        <form
          className="w-[80%] mx-auto"
          onSubmit={methods.handleSubmit(() => {
            setCurrentStep((step) => step + 1);
          })}
        >
          <div className="flex space-x-3">
            <div className="flex-1">
              <FieldConnector
                className="mt-3"
                name={AccountSettingsFieldsNamesEnum.firstName}
                label={t('settings.personalDetails.firstName')}
              >
                {({ field }) => (
                  <FreeTextInput
                    type="text"
                    {...field}
                    placeholder={t(
                      'settings.personalDetails.firstNamePlaceholder',
                    )}
                  />
                )}
              </FieldConnector>
            </div>

            <div className="flex-1">
              <FieldConnector
                className="mt-3"
                name={AccountSettingsFieldsNamesEnum.lastName}
                label={t('settings.personalDetails.lastName')}
              >
                {({ field }) => (
                  <FreeTextInput
                    type="text"
                    {...field}
                    placeholder={t(
                      'settings.personalDetails.lastNamePlaceholder',
                    )}
                  />
                )}
              </FieldConnector>
            </div>
          </div>

          <FieldConnector
            className="mt-3"
            name={AccountSettingsFieldsNamesEnum.companyName}
            label={t('settings.companyDetails.companyName')}
            tooltip={t('signup.company_name.field.explanation')}
          >
            {({ field }) => (
              <FreeTextInput
                {...field}
                placeholder={t(
                  'settings.personalDetails.companyNamePlaceholder',
                )}
              />
            )}
          </FieldConnector>

          <FieldConnector
            className="mt-3"
            name={AccountSettingsFieldsNamesEnum.email}
            label={t('settings.personalDetails.email')}
          >
            {({ field }) => (
              <FreeTextInput
                type="email"
                {...field}
                placeholder={t('settings.personalDetails.emailPlaceholder')}
              />
            )}
          </FieldConnector>

          <h3 className="mb-1 mt-5 text-primary font-semibold">
            {t('settings.companyDetails.vatNumber')}
          </h3>

          <FieldConnector
            className="mt-3 flex-1"
            name={AccountSettingsFieldsNamesEnum.VATNumber}
          >
            {({ field, name }) => (
              <FreeTextInput
                errors={methods.formState.errors}
                placeholder={vatNumberPlaceholder}
                name={name}
                type="maskedInput"
                maskProps={{
                  maskChar: null,
                  mask: VATNumberMask,
                  normalizer: VATNumberMaskNormalizer,
                }}
                value={field.value}
                onChange={({ target: { value: inputValue } }) =>
                  field.onChange(normalizeMaskedInputs(inputValue))
                }
              />
            )}
          </FieldConnector>

          <h3 className="mb-1 mt-5 text-primary font-semibold">
            {t('accountable_banking.onboarding.form.address.heading')}
          </h3>

          <div className="flex space-x-3">
            <FieldConnector
              name={AccountSettingsFieldsNamesEnum.country}
              className="flex-1"
            >
              {({ field, name, label }) => (
                <Select
                  name={name}
                  isDisabled
                  value={
                    getSelectOptionValue(
                      settingsOptions.countriesOptions,
                      field.value,
                    )?.value
                  }
                  // components={{ SingleValue: CountryOption } as any}
                  // styles={{
                  //   valueContainer: () => ({
                  //     display: 'flex',
                  //     paddingLeft: 10,
                  //   }),
                  // }}
                  formatOptionLabel={(data) => {
                    return (
                      <div className="flex items-center">
                        <CountryFlag country={data.value} className="h-6 w-6" />
                        <p className="ml-4">{data.label}</p>
                      </div>
                    );
                  }}
                  placeholder={label}
                  options={settingsOptions.countriesOptions}
                  onChange={(value) => field.onChange(value)}
                />
              )}
            </FieldConnector>

            <FieldConnector
              className="flex-1"
              name={AccountSettingsFieldsNamesEnum.city}
            >
              {({ field }) => (
                <FreeTextInput
                  {...field}
                  placeholder={t(`settings.companyDetails.city`)}
                />
              )}
            </FieldConnector>
          </div>

          <div className="flex space-x-3 mt-3">
            <FieldConnector
              className="flex-1"
              name={AccountSettingsFieldsNamesEnum.street}
            >
              {({ field }) => (
                <FreeTextInput
                  {...field}
                  placeholder={t(`settings.companyDetails.street`)}
                />
              )}
            </FieldConnector>

            <FieldConnector
              className="flex-1"
              name={AccountSettingsFieldsNamesEnum.zip}
            >
              {({ field }) => (
                <FreeTextInput
                  {...field}
                  placeholder={t(`settings.companyDetails.postalCode`)}
                />
              )}
            </FieldConnector>
          </div>

          <Alert
            className="mt-3"
            type="info"
            description={t(
              'accountable_banking.onboarding.form.bussiness_registry_address_banner.content',
            )}
          />

          <h3 className="mb-1 mt-5 text-primary font-semibold">
            {t('accountable_banking.onboarding.form.activity.heading')}
          </h3>

          <FieldConnector
            name={[
              AccountSettingsFieldsNamesEnum.APC1,
              AccountSettingsFieldsNamesEnum.APC2,
              AccountSettingsFieldsNamesEnum.occupation,
            ]}
          >
            {({ field: { error, ...field } }) => (
              <ProfessionSelect
                {...field}
                creatable
                error={error}
                controlShouldRenderValue={APC2_value ? true : undefined}
                isClearable
                themeOptions={{
                  hideDropdownIndicator: true,
                }}
                value={
                  occupation_value || { APC1: APC1_value, APC2: APC2_value }
                }
                onChange={(option) => {
                  if (!option) {
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC1, null);
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC2, null);
                    setAndValidate(
                      AccountSettingsFieldsNamesEnum.occupation,
                      null,
                    );
                    return;
                  }
                  if (option.__isCreated) {
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC1, null);
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC2, null);
                    setAndValidate(
                      AccountSettingsFieldsNamesEnum.occupation,
                      option.value,
                    );
                    return;
                  }
                  const data = option as APC;
                  setAndValidate(
                    AccountSettingsFieldsNamesEnum.APC1,
                    data[AccountSettingsFieldsNamesEnum.APC1],
                  );
                  setAndValidate(
                    AccountSettingsFieldsNamesEnum.APC2,
                    data[AccountSettingsFieldsNamesEnum.APC2],
                  );
                  setAndValidate(
                    AccountSettingsFieldsNamesEnum.occupation,
                    null,
                  );
                }}
              />
            )}
          </FieldConnector>

          <FieldConnector
            name={
              AccountSettingsFieldsNamesEnum.additionalBusinessActivityDescription
            }
            className="mt-3"
            label={t(
              'accountable_banking.onboarding.form.additional_activity_details.label',
            )}
          >
            {({ field }) => <FreeTextInput {...field} />}
          </FieldConnector>

          <FieldConnector
            name={AccountSettingsFieldsNamesEnum.monthlyPaymentVolume}
            className="mt-3"
            label={t(
              'accountable_banking.onboarding.form.estimate_volume.label',
            )}
            hint={t('accountable_banking.onboarding.form.estimate_volume.hint')}
          >
            {({ field, name, label }) => (
              <Select
                {...field}
                name={name}
                placeholder={label}
                options={[
                  {
                    label: t('less_than_amount', {
                      amount: formatMoney(10000),
                    }),
                    value: MonthlyPaymentVolume.LessThan10000,
                  },
                  {
                    label: t('between_amounts', {
                      amount1: formatMoney(10000),
                      amount2: formatMoney(50000),
                    }),
                    value: MonthlyPaymentVolume.Between10000And50000,
                  },
                  {
                    label: t('between_amounts', {
                      amount1: formatMoney(50000),
                      amount2: formatMoney(100000),
                    }),
                    value: MonthlyPaymentVolume.Between50000And100000,
                  },
                  {
                    label: t('more_than_amount', {
                      amount: formatMoney(100000),
                    }),
                    value: MonthlyPaymentVolume.MoreThan100000,
                  },
                ]}
                onChange={({ value }) => field.onChange(value)}
              />
            )}
          </FieldConnector>

          <div className="text-center">
            <Button
              type="submit"
              color="bank"
              className="my-6 mt-12 w-full capitalize"
            >
              {t('settings.controllers.save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </motion.div>,

    <motion.div {...fadeTransition} key="form_2">
      <ModalHeading
        Icon={FileSearchIcon}
        title={t('accountable_banking.onboarding.review_info.title')}
        subtitle={t('accountable_banking.onboarding.review_info.subtitle')}
      />

      <div className="flex items-center justify-between mt-7">
        <h2 className="text-lg font-semibold text-primary-700">
          {t('personal_info')}
        </h2>

        <Button
          asDefault
          onClick={() => setCurrentStep((step) => step - 1)}
          className="font-semibold underline text-primary-700"
        >
          {t('edit')}
        </Button>
      </div>

      <div className="my-4 text-center border border-taxes-100 rounded-lg w-full">
        <div className="flex items-center justify-between p-5 border-b border-taxes-100">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700 capitalize">{t('name')}</h3>

            <p className="text-primary font-semibold text-lg">
              {`${firstNameValue} ${lastNameValue}`}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-5 border-b border-taxes-100">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700 capitalize">{t('email')}</h3>

            <p className="text-primary font-semibold text-lg">{emailValue}</p>
          </div>
        </div>

        <div className="flex items-center justify-between p-5 border-b border-taxes-100">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700">
              {t('settings.companyDetails.vatNumber')}
            </h3>

            <p className="text-primary font-semibold text-lg">
              {VATNumberValue}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-5 border-b border-taxes-100">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700">{t('address.address')}</h3>

            <p className="text-primary font-semibold text-lg">
              {`${streetValue}, ${zipValue}, ${cityValue}, ${t(
                `country.${countryValue}`,
              )}`}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-5">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700">
              {t('accountable_banking.onboarding.review_info.activity.label')}
            </h3>

            <p className="text-primary font-semibold text-lg first-letter:capitalize">
              {t(`${user?.country}.APC2.${APC2_value}`)}
            </p>
          </div>
        </div>
      </div>

      <div className="text-center">
        <Button
          color="bank"
          className="my-6 w-full capitalize"
          onClick={() => onConfirm(methods.getValues())}
        >
          {t('next')}
        </Button>
      </div>
    </motion.div>,
  ];

  return <AnimatePresence mode="wait">{steps[currentStep]}</AnimatePresence>;
};

const DEForm: FC<{
  onConfirm: (values: OnboardingFormValues) => void;
  isConfirming: boolean;
  currentAccountDetailsValues?: IUser;
}> = ({ onConfirm, currentAccountDetailsValues, isConfirming }) => {
  const { t } = useTranslation();

  const user = useGetCurrentCustomerData().data as IUser;

  const defaultValues = useMemo(
    () =>
      constructAccountDetailsFormDefaultValues(
        currentAccountDetailsValues || (user as IUser),
      ),
    [currentAccountDetailsValues, user],
  );

  const [currentStep, setCurrentStep] = useState(0);

  const schema = DEOnboardingFormSchema();

  const methods = useForm({
    defaultValues,
    resolver: joiResolver(schema, {
      allowUnknown: true,
      abortEarly: false,
    }),
  });

  const setAndValidate: typeof methods.setValue = (name, value, options) =>
    methods.setValue(name, value, { shouldValidate: true, ...options });

  const settingsOptions = useSettingsSelectOptions(
    user?.country as AvailableCountries,
  );

  const [
    APC1_value,
    APC2_value,
    occupation_value,
    steuernummerValue,
    streetValue,
    cityValue,
    countryValue,
    zipValue,
    firstNameValue,
    lastNameValue,
    emailValue,
  ] = methods.watch([
    AccountSettingsFieldsNamesEnum.APC1,
    AccountSettingsFieldsNamesEnum.APC2,
    AccountSettingsFieldsNamesEnum.occupation,
    AccountSettingsFieldsNamesEnum.steuernummer,
    AccountSettingsFieldsNamesEnum.street,
    AccountSettingsFieldsNamesEnum.city,
    AccountSettingsFieldsNamesEnum.country,
    AccountSettingsFieldsNamesEnum.zip,
    AccountSettingsFieldsNamesEnum.firstName,
    AccountSettingsFieldsNamesEnum.lastName,
    AccountSettingsFieldsNamesEnum.email,
  ]);

  const steps = [
    <motion.div {...fadeTransition} key="form_1" className="w-full">
      <ModalHeading
        Icon={WalletIcon}
        title={t('accountable_banking.onboarding.form.title')}
        subtitle={t('accountable_banking.onboarding.form.subtitle')}
      />

      <FormProvider {...methods}>
        <form
          className="w-[80%] mx-auto"
          onSubmit={methods.handleSubmit(() => {
            setCurrentStep((step) => step + 1);
          })}
        >
          <div className="flex space-x-3">
            <div className="flex-1">
              <FieldConnector
                className="mt-3"
                name={AccountSettingsFieldsNamesEnum.firstName}
                label={t('settings.personalDetails.firstName')}
              >
                {({ field }) => (
                  <FreeTextInput
                    type="text"
                    {...field}
                    placeholder={t(
                      'settings.personalDetails.firstNamePlaceholder',
                    )}
                  />
                )}
              </FieldConnector>
            </div>

            <div className="flex-1">
              <FieldConnector
                className="mt-3"
                name={AccountSettingsFieldsNamesEnum.lastName}
                label={t('settings.personalDetails.lastName')}
              >
                {({ field }) => (
                  <FreeTextInput
                    type="text"
                    {...field}
                    placeholder={t(
                      'settings.personalDetails.lastNamePlaceholder',
                    )}
                  />
                )}
              </FieldConnector>
            </div>
          </div>

          <FieldConnector
            className="mt-3"
            name={AccountSettingsFieldsNamesEnum.companyName}
            label={t('settings.companyDetails.companyName')}
            tooltip={t('signup.company_name.field.explanation')}
          >
            {({ field }) => (
              <FreeTextInput
                {...field}
                placeholder={t(
                  'settings.personalDetails.companyNamePlaceholder',
                )}
              />
            )}
          </FieldConnector>

          <FieldConnector
            className="mt-3"
            name={AccountSettingsFieldsNamesEnum.email}
            label={t('settings.personalDetails.email')}
          >
            {({ field }) => (
              <FreeTextInput
                type="email"
                {...field}
                placeholder={t('settings.personalDetails.emailPlaceholder')}
              />
            )}
          </FieldConnector>

          <h3 className="mb-1 mt-5 text-primary font-semibold">
            {t('accountable_banking.onboarding.form.address.heading')}
          </h3>

          <FieldConnector name={AccountSettingsFieldsNamesEnum.country}>
            {({ field, name, label }) => (
              <Select
                name={name}
                isDisabled
                value={
                  getSelectOptionValue(
                    settingsOptions.countriesOptions,
                    field.value,
                  )?.value
                }
                // components={{ SingleValue: CountryOption } as any}
                // styles={{
                //   valueContainer: () => ({
                //     display: 'flex',
                //     paddingLeft: 10,
                //   }),
                // }}
                formatOptionLabel={(data) => {
                  return (
                    <div className="flex items-center">
                      <CountryFlag country={data.value} className="h-6 w-6" />
                      <p className="ml-4">{data.label}</p>
                    </div>
                  );
                }}
                placeholder={label}
                options={settingsOptions.countriesOptions}
                onChange={(value) => field.onChange(value)}
              />
            )}
          </FieldConnector>

          <div className="flex items-start my-3 space-x-3">
            <FieldConnector
              className="flex-1"
              name={AccountSettingsFieldsNamesEnum.state}
            >
              {({ name, field }) => (
                <Select
                  {...field}
                  name={name}
                  placeholder={t('settings.companyDetails.state')}
                  options={settingsOptions.germanStatesOptions}
                  value={
                    getSelectOptionValue(
                      settingsOptions.germanStatesOptions,
                      field.value,
                    )?.value
                  }
                  onChange={({ value }: any) => {
                    field.onChange(value);
                    methods.clearErrors(name as any);
                  }}
                />
              )}
            </FieldConnector>

            <FieldConnector
              className="flex-1"
              name={AccountSettingsFieldsNamesEnum.city}
            >
              {({ field }) => (
                <FreeTextInput
                  {...field}
                  placeholder={t(`settings.companyDetails.city`)}
                />
              )}
            </FieldConnector>
          </div>

          <div className="flex items-start my-3 space-x-3">
            <FieldConnector
              className="flex-1"
              name={AccountSettingsFieldsNamesEnum.street}
            >
              {({ field }) => (
                <FreeTextInput
                  {...field}
                  placeholder={t(`settings.companyDetails.street`)}
                />
              )}
            </FieldConnector>

            <FieldConnector
              className="flex-1"
              name={AccountSettingsFieldsNamesEnum.zip}
            >
              {({ field }) => (
                <FreeTextInput
                  {...field}
                  placeholder={t(`settings.companyDetails.postalCode`)}
                />
              )}
            </FieldConnector>
          </div>

          <Alert
            className="mt-3"
            type="info"
            description={t(
              'accountable_banking.onboarding.form.bussiness_registry_address_banner.content',
            )}
          />

          <h3 className="mb-1 mt-5 text-primary font-semibold">
            {t('settings.companyDetails.steuernummer')}
          </h3>

          <FieldConnector
            className="flex-1"
            name={AccountSettingsFieldsNamesEnum.steuernummer}
            hint="10-11 digits"
          >
            {({ field }) => (
              <SteuernummerField
                {...field}
                stateFieldName={AccountSettingsFieldsNamesEnum.state}
              />
            )}
          </FieldConnector>

          <h3 className="mb-1 mt-5 text-primary font-semibold">
            {t('accountable_banking.onboarding.form.activity.heading')}
          </h3>

          <FieldConnector
            name={[
              AccountSettingsFieldsNamesEnum.APC1,
              AccountSettingsFieldsNamesEnum.APC2,
              AccountSettingsFieldsNamesEnum.occupation,
            ]}
          >
            {({ field: { error, ...field } }) => (
              <ProfessionSelect
                {...field}
                creatable
                error={error}
                controlShouldRenderValue={APC2_value ? true : undefined}
                isClearable
                themeOptions={{
                  hideDropdownIndicator: true,
                }}
                value={
                  occupation_value || { APC1: APC1_value, APC2: APC2_value }
                }
                onChange={(option) => {
                  if (!option) {
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC1, null);
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC2, null);
                    setAndValidate(
                      AccountSettingsFieldsNamesEnum.occupation,
                      null,
                    );
                    return;
                  }
                  if (option.__isCreated) {
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC1, null);
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC2, null);
                    setAndValidate(
                      AccountSettingsFieldsNamesEnum.occupation,
                      option.value,
                    );
                    return;
                  }
                  const data = option as APC;
                  setAndValidate(
                    AccountSettingsFieldsNamesEnum.APC1,
                    data[AccountSettingsFieldsNamesEnum.APC1],
                  );
                  setAndValidate(
                    AccountSettingsFieldsNamesEnum.APC2,
                    data[AccountSettingsFieldsNamesEnum.APC2],
                  );
                  setAndValidate(
                    AccountSettingsFieldsNamesEnum.occupation,
                    null,
                  );
                }}
              />
            )}
          </FieldConnector>

          <FieldConnector
            name={
              AccountSettingsFieldsNamesEnum.additionalBusinessActivityDescription
            }
            className="mt-3"
            label={t(
              'accountable_banking.onboarding.form.additional_activity_details.label',
            )}
          >
            {({ field }) => <FreeTextInput {...field} />}
          </FieldConnector>

          <FieldConnector
            name={AccountSettingsFieldsNamesEnum.monthlyPaymentVolume}
            className="mt-3"
            label={t(
              'accountable_banking.onboarding.form.estimate_volume.label',
            )}
            hint={t('accountable_banking.onboarding.form.estimate_volume.hint')}
          >
            {({ field, name, label }) => (
              <Select
                {...field}
                name={name}
                placeholder={label}
                options={[
                  {
                    label: t('less_than_amount', {
                      amount: formatMoney(10000),
                    }),
                    value: MonthlyPaymentVolume.LessThan10000,
                  },
                  {
                    label: t('between_amounts', {
                      amount1: formatMoney(10000),
                      amount2: formatMoney(50000),
                    }),
                    value: MonthlyPaymentVolume.Between10000And50000,
                  },
                  {
                    label: t('between_amounts', {
                      amount1: formatMoney(50000),
                      amount2: formatMoney(100000),
                    }),
                    value: MonthlyPaymentVolume.Between50000And100000,
                  },
                  {
                    label: t('more_than_amount', {
                      amount: formatMoney(100000),
                    }),
                    value: MonthlyPaymentVolume.MoreThan100000,
                  },
                ]}
                onChange={({ value }) => field.onChange(value)}
              />
            )}
          </FieldConnector>

          <div className="text-center">
            <Button
              type="submit"
              color="bank"
              className="my-6 mt-12 w-full capitalize"
            >
              {t('settings.controllers.save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </motion.div>,

    <motion.div {...fadeTransition} key="form_2">
      <ModalHeading
        Icon={FileSearchIcon}
        title={t('accountable_banking.onboarding.review_info.title')}
        subtitle={t('accountable_banking.onboarding.review_info.subtitle')}
      />

      <div className="flex items-center justify-between mt-7">
        <h2 className="text-lg font-semibold text-primary-700">
          {t('personal_info')}
        </h2>

        <Button
          asDefault
          onClick={() => setCurrentStep((step) => step - 1)}
          className="font-semibold underline text-primary-700"
        >
          {t('edit')}
        </Button>
      </div>

      <div className="my-4 text-center border border-taxes-100 rounded-lg w-full">
        <div className="flex items-center justify-between p-5 border-b border-taxes-100">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700 capitalize">{t('name')}</h3>

            <p className="text-primary font-semibold text-lg">
              {`${firstNameValue} ${lastNameValue}`}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-5 border-b border-taxes-100">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700 capitalize">{t('email')}</h3>

            <p className="text-primary font-semibold text-lg">{emailValue}</p>
          </div>
        </div>

        <div className="flex items-center justify-between p-5 border-b border-taxes-100">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700">
              {t('settings.companyDetails.steuernummer')}
            </h3>

            <p className="text-primary font-semibold text-lg">
              {steuernummerValue}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-5 border-b border-taxes-100">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700">{t('address.address')}</h3>

            <p className="text-primary font-semibold text-lg">
              {`${streetValue}, ${zipValue}, ${cityValue}, ${t(
                `country.${countryValue}`,
              )}`}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-5">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700">
              {t('accountable_banking.onboarding.review_info.activity.label')}
            </h3>

            <p className="text-primary font-semibold text-lg first-letter:capitalize">
              {t(`${user?.country}.APC2.${APC2_value}`)}
            </p>
          </div>
        </div>
      </div>

      <div className="text-center">
        <Button
          color="bank"
          className="my-6 w-full capitalize"
          loading={isConfirming}
          onClick={() => onConfirm(methods.getValues())}
        >
          {t('next')}
        </Button>
      </div>
    </motion.div>,
  ];

  return <AnimatePresence mode="wait">{steps[currentStep]}</AnimatePresence>;
};

const OnboardingForm: FC<{
  onConfirm: (values: OnboardingFormValues) => void;
  currentAccountDetailsValues?: IUser;
  isConfirming: boolean;
}> = ({ onConfirm, currentAccountDetailsValues, isConfirming }) => {
  const user = useGetCurrentCustomerData().data;

  const FormTag =
    user?.country === AvailableCountries.belgium ? BEForm : DEForm;

  return (
    <motion.div
      {...fadeTransition}
      className="flex items-center justify-center flex-col relative"
      key="3"
    >
      <FormTag
        onConfirm={onConfirm}
        isConfirming={isConfirming}
        currentAccountDetailsValues={currentAccountDetailsValues}
      />
    </motion.div>
  );
};

export default OnboardingForm;
